if (!customElements.get('dyefor-latest-designs')) {
  customElements.define('dyefor-latest-designs', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-latest-track]');
      this.items = [...this.querySelectorAll('[data-latest-item]')];
      if (!this.track || !this.items.length) return;
      this.controller = new AbortController();
      const listen = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = this.dataset.autoplay === 'true' && !this.reducedMotion.matches && !window.Shopify?.designMode;
      this.visible = false;
      this.hovered = false;
      this.direction = 1;
      this.previous = this.querySelector('[data-latest-prev]');
      this.next = this.querySelector('[data-latest-next]');
      this.motion = this.querySelector('[data-latest-motion]');
      listen(this.previous, 'click', () => this.move(-1, true));
      listen(this.next, 'click', () => this.move(1, true));
      if (this.motion) listen(this.motion, 'click', () => {
        this.playing = !this.playing;
        this.renderMotion();
        this.schedule();
      });
      listen(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true });
      listen(this.track, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.pause();
        if (event.key === 'Home' || event.key === 'End') this.go(event.key === 'Home' ? 0 : this.maxScroll, true);
        else this.move(event.key === 'ArrowRight' ? 1 : -1, true);
      });
      listen(this.track, 'pointerdown', () => this.pause(), { passive: true });
      listen(this.track, 'wheel', event => {
        if (Math.abs(event.deltaX) > 0) this.pause();
      }, { passive: true });
      listen(this, 'pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => this.schedule());
      listen(this, 'focusout', () => queueMicrotask(() => this.schedule()));
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.reducedMotion, 'change', () => {
        if (this.reducedMotion.matches) this.pause();
      });
      this.resizeObserver = new ResizeObserver(() => { this.update(); this.schedule(); });
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= .3;
        this.schedule();
      }, { threshold: [0, .3] });
      this.intersectionObserver.observe(this);
      this.classList.add('is-ready');
      this.update();
      this.renderMotion();
    }

    get maxScroll() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }

    pause() {
      this.playing = false;
      this.renderMotion();
      clearTimeout(this.timer);
    }

    renderMotion() {
      if (!this.motion) return;
      const label = this.playing ? 'Pause slideshow' : 'Play slideshow';
      this.motion.setAttribute('aria-label', label);
      this.motion.title = label;
      this.motion.querySelector('[data-latest-pause]').hidden = !this.playing;
      this.motion.querySelector('[data-latest-play]').hidden = this.playing;
    }

    update() {
      const max = this.maxScroll;
      const left = this.track.scrollLeft;
      this.previous.disabled = left <= 2;
      this.next.disabled = left >= max - 2;
      this.querySelector('[data-latest-controls]').hidden = max <= 2;
      const start = this.items[0].offsetLeft;
      this.index = this.items.reduce((nearest, item, index) =>
        Math.abs(item.offsetLeft - start - left) < Math.abs(this.items[nearest].offsetLeft - start - left) ? index : nearest, 0);
      this.querySelector('[data-latest-position]').textContent = String(this.index + 1).padStart(2, '0');
      const progress = Math.min(1, (left + this.track.clientWidth) / this.track.scrollWidth);
      this.querySelector('[data-latest-progress]').style.width = `${progress * 100}%`;
    }

    move(direction, manual = false) {
      if (manual) this.pause();
      const step = this.items.length > 1 ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.track.clientWidth;
      this.go(Math.max(0, Math.min(this.maxScroll, this.track.scrollLeft + direction * step)), manual);
    }

    go(left, announce = false) {
      this.track.scrollTo({ left, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      if (announce) {
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => {
          if (!this.isConnected) return;
          this.update();
          this.querySelector('[data-latest-status]').textContent = `${this.items[this.index].querySelector('h3').textContent.trim()}, design ${this.index + 1} of ${this.items.length}`;
        }, 700);
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.playing || !this.visible || this.hovered || document.hidden || this.contains(document.activeElement) || this.maxScroll <= 2) return;
      this.timer = setTimeout(() => {
        // Reverse at either end, keeping real product links and avoiding duplicate slides.
        if (this.track.scrollLeft >= this.maxScroll - 2) this.direction = -1;
        if (this.track.scrollLeft <= 2) this.direction = 1;
        this.move(this.direction);
        this.schedule();
      }, Math.max(4000, Number(this.dataset.interval) || 5000));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.announceTimer);
      cancelAnimationFrame(this.frame);
    }
  });
}
